// Per-block edit forms — port of the artifact's EDIT_FORMS object.

import { useState } from 'react';
import { Plus, Trash2, ArrowUp, ArrowDown, Cloud, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { BlockType, StoryBodyItem } from '@shared/types';
import RichTextArea from './RichTextArea';
import { fetchWeather } from './weather-api';
import { ImagePicker } from './ImagePicker';
import { BodyEditor } from './BodyEditor';
import { storyBodyItems, resolveCalendarMode, resolveCalendarEvents } from './blocks';
import { useUpcomingEvents } from './UpcomingEventsContext';
import { formatEventDate } from '@shared/event-date';

// ============================================================
// Date helpers — used by HeaderForm to round-trip between the
// human-readable date line and the YYYY-MM-DD format that
// `<input type="date">` expects.
// ============================================================
function isoFromDateLine(s: string | undefined): string {
  if (!s) return '';
  // Try parsing the human-readable form. Strip the leading "Day · " part
  // because Date.parse handles "April 25, 2026" but not the bullet-prefix.
  const cleaned = s.replace(/^[^·]+·\s*/, '').trim();
  const d = new Date(cleaned);
  if (isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatHumanDate(iso: string): string {
  // Avoid the timezone footgun: parse as local midnight.
  const d = new Date(iso + 'T00:00:00');
  const day = d.toLocaleDateString('en-US', { weekday: 'long' });
  const month = d.toLocaleDateString('en-US', { month: 'long' });
  return `${day} · ${month} ${d.getDate()}, ${d.getFullYear()}`;
}

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export const Field = ({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) => (
  <div className="mb-4">
    <label className="block text-xs font-semibold text-slate-700 mb-1.5 tracking-wide uppercase">{label}</label>
    {children}
    {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
  </div>
);

export const TextInput = ({ value, onChange, placeholder }: { value?: string; onChange: (v: string) => void; placeholder?: string }) => (
  <input
    type="text"
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500 bg-white"
  />
);

export const TextArea = ({ value, onChange, placeholder, rows = 3 }: { value?: string; onChange: (v: string) => void; placeholder?: string; rows?: number }) => (
  <textarea
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    rows={rows}
    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500 bg-white resize-y"
  />
);

const WEATHER_ICONS: Array<{ value: string; label: string }> = [
  { value: '', label: '(no emoji)' },
  { value: '☀', label: '☀ Sunny' },
  { value: '⛅', label: '⛅ Partly cloudy' },
  { value: '☁', label: '☁ Cloudy' },
  { value: '🌫', label: '🌫 Foggy' },
  { value: '🌧', label: '🌧 Rain' },
  { value: '❄', label: '❄ Snow' },
  { value: '⛈', label: '⛈ Thunderstorm' },
];

export const WeatherIconSelect = ({ value, onChange, compact }: { value?: string; onChange: (v: string) => void; compact?: boolean }) => {
  const v = value ?? '';
  const known = WEATHER_ICONS.some((opt) => opt.value === v);
  const cls = compact
    ? 'w-full px-2 py-1.5 text-xs border border-slate-200 rounded bg-white'
    : 'w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500 bg-white';
  return (
    <select value={v} onChange={(e) => onChange(e.target.value)} className={cls}>
      {WEATHER_ICONS.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
      {!known && <option value={v}>{v} (custom)</option>}
    </select>
  );
};

interface ArrayEditorProps<T> {
  items: T[];
  onChange: (next: T[]) => void;
  renderItem: (item: T, update: (next: T) => void, idx: number) => React.ReactNode;
  addLabel?: string;
  defaultItem: () => T;
}

export function ArrayEditor<T>({ items = [], onChange, renderItem, addLabel = 'Add item', defaultItem }: ArrayEditorProps<T>) {
  return (
    <div className="space-y-3">
      {items.map((item, idx) => (
        <div key={idx} className="border border-slate-200 rounded-md p-3 bg-slate-50 relative group">
          <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {idx > 0 && (
              <button onClick={() => { const next = [...items]; [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]]; onChange(next); }} className="p-1 hover:bg-white rounded text-slate-500 hover:text-slate-700">
                <ArrowUp size={12} />
              </button>
            )}
            {idx < items.length - 1 && (
              <button onClick={() => { const next = [...items]; [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]]; onChange(next); }} className="p-1 hover:bg-white rounded text-slate-500 hover:text-slate-700">
                <ArrowDown size={12} />
              </button>
            )}
            <button onClick={() => onChange(items.filter((_, i) => i !== idx))} className="p-1 hover:bg-white rounded text-slate-500 hover:text-red-600">
              <Trash2 size={12} />
            </button>
          </div>
          {renderItem(item, (next) => { const arr = [...items]; arr[idx] = next; onChange(arr); }, idx)}
        </div>
      ))}
      <button onClick={() => onChange([...items, defaultItem()])} className="w-full py-2 text-xs font-semibold text-blue-700 border border-dashed border-blue-300 rounded-md hover:bg-blue-50 flex items-center justify-center gap-1.5">
        <Plus size={12} /> {addLabel}
      </button>
    </div>
  );
}

type FormProps = { data: any; onChange: (data: any) => void; draftId: string };

const HeaderForm = ({ data, onChange }: FormProps) => {
  const u = (k: string, v: unknown) => onChange({ ...data, [k]: v });
  const mode = data.editionMode === 'custom' ? 'custom' : 'numbered';
  return (
    <>
      <Field label="Logo URL" hint="Hosted image. Recommend 480×96 (2x).">
        <TextInput value={data.logoUrl} onChange={(v) => u('logoUrl', v)} />
      </Field>
      <Field label="Date" hint="Pick a date or type your own format below.">
        <div className="flex gap-2 items-center">
          <input
            type="date"
            value={isoFromDateLine(data.dateLine)}
            onChange={(e) => { if (e.target.value) u('dateLine', formatHumanDate(e.target.value)); }}
            className="px-2 py-1.5 text-sm border border-slate-200 rounded-md bg-white"
          />
          <TextInput value={data.dateLine} onChange={(v) => u('dateLine', v)} placeholder="Friday · April 25, 2026" />
        </div>
      </Field>
      <Field label="Edition number" hint="Numbered shows 'Edition Nº 042'. Custom replaces it entirely with whatever you type.">
        <div className="flex gap-2 mb-2">
          <button
            type="button"
            onClick={() => u('editionMode', 'numbered')}
            className={`flex-1 px-3 py-2 text-sm rounded-md border transition-colors ${mode === 'numbered' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 hover:border-slate-300 text-slate-600'}`}
          >
            Numbered
          </button>
          <button
            type="button"
            onClick={() => u('editionMode', 'custom')}
            className={`flex-1 px-3 py-2 text-sm rounded-md border transition-colors ${mode === 'custom' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 hover:border-slate-300 text-slate-600'}`}
          >
            Custom
          </button>
        </div>
        {mode === 'numbered' ? (
          <input
            type="number"
            min={1}
            value={(() => {
              const n = parseInt(String(data.editionNumber || ''), 10);
              return Number.isFinite(n) ? n : '';
            })()}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              if (!Number.isNaN(n)) u('editionNumber', String(n).padStart(3, '0'));
            }}
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500 bg-white"
          />
        ) : (
          <TextInput value={data.editionNumber} onChange={(v) => u('editionNumber', v)} placeholder="SPECIAL EDITION" />
        )}
      </Field>
    </>
  );
};

const GreetingForm = ({ data, onChange }: FormProps) => {
  const u = (k: string, v: unknown) => onChange({ ...data, [k]: v });
  return (
    <>
      <Field label="Lead phrase" hint='e.g. "Good morning, Bangor."'>
        <TextInput value={data.lead} onChange={(v) => u('lead', v)} />
      </Field>
      <Field label="Body">
        <RichTextArea value={data.body || ''} onChange={(v) => u('body', v)} rows={5} />
      </Field>
      <Field label="Signature"><TextInput value={data.signature} onChange={(v) => u('signature', v)} /></Field>
    </>
  );
};

const WeatherForm = ({ data, onChange }: FormProps) => {
  const u = (k: string, v: unknown) => onChange({ ...data, [k]: v });
  const [pullDate, setPullDate] = useState(todayIso());
  const [pulling, setPulling] = useState(false);
  const [pullErr, setPullErr] = useState<string | null>(null);

  async function pull() {
    setPullErr(null);
    setPulling(true);
    try {
      const w = await fetchWeather(pullDate);
      onChange({ ...data, ...w });
    } catch (e) {
      setPullErr(e instanceof Error ? e.message : 'Fetch failed');
    } finally {
      setPulling(false);
    }
  }

  return (
    <>
      <div className="border border-blue-100 bg-blue-50/50 rounded-md p-3 mb-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-blue-700 mb-2">Auto-fill from Open-Meteo</div>
        <div className="flex gap-2 items-center">
          <input
            type="date"
            value={pullDate}
            onChange={(e) => setPullDate(e.target.value)}
            className="px-2 py-1.5 text-sm border border-slate-200 rounded-md bg-white"
          />
          <button
            type="button"
            onClick={pull}
            disabled={pulling || !pullDate}
            className="btn-secondary text-xs py-1.5"
          >
            {pulling ? <Loader2 size={12} className="animate-spin" /> : <Cloud size={12} />}
            {pulling ? 'Fetching…' : 'Pull weather'}
          </button>
        </div>
        <p className="text-[11px] text-slate-500 mt-1.5">Bangor, ME · No API key required. Overwrites the fields below — manual edits below still win on save.</p>
        {pullErr && <p className="text-xs text-red-600 mt-1">{pullErr}</p>}
      </div>

      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 mt-1">Today</div>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Temp"><TextInput value={data.todayTemp} onChange={(v) => u('todayTemp', v)} /></Field>
        <Field label="Icon"><WeatherIconSelect value={data.todayIcon} onChange={(v) => u('todayIcon', v)} /></Field>
      </div>
      <Field label="Conditions"><TextInput value={data.todayLabel} onChange={(v) => u('todayLabel', v)} /></Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="High"><TextInput value={data.todayHigh} onChange={(v) => u('todayHigh', v)} /></Field>
        <Field label="Low"><TextInput value={data.todayLow} onChange={(v) => u('todayLow', v)} /></Field>
      </div>

      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 mt-4">Tomorrow</div>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Temp"><TextInput value={data.tomorrowTemp} onChange={(v) => u('tomorrowTemp', v)} /></Field>
        <Field label="Icon"><WeatherIconSelect value={data.tomorrowIcon} onChange={(v) => u('tomorrowIcon', v)} /></Field>
      </div>
      <Field label="Conditions"><TextInput value={data.tomorrowLabel} onChange={(v) => u('tomorrowLabel', v)} /></Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="High"><TextInput value={data.tomorrowHigh} onChange={(v) => u('tomorrowHigh', v)} /></Field>
        <Field label="Low"><TextInput value={data.tomorrowLow} onChange={(v) => u('tomorrowLow', v)} /></Field>
      </div>

      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 mt-4">Forecast (rest of week)</div>
      <ArrayEditor
        items={data.forecast || []}
        onChange={(v) => u('forecast', v)}
        defaultItem={() => ({ day: 'Day', high: '50°', low: '40°', icon: '☀' })}
        addLabel="Add forecast day"
        renderItem={(item: any, update) => (
          <div className="grid grid-cols-4 gap-2">
            <input className="px-2 py-1.5 text-xs border border-slate-200 rounded bg-white" value={item.day} onChange={(e) => update({ ...item, day: e.target.value })} placeholder="Day" />
            <input className="px-2 py-1.5 text-xs border border-slate-200 rounded bg-white" value={item.high} onChange={(e) => update({ ...item, high: e.target.value })} placeholder="High" />
            <input className="px-2 py-1.5 text-xs border border-slate-200 rounded bg-white" value={item.low} onChange={(e) => update({ ...item, low: e.target.value })} placeholder="Low" />
            <WeatherIconSelect value={item.icon} onChange={(v) => update({ ...item, icon: v })} compact />
          </div>
        )}
      />

      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 mt-4">Sunrise / Sunset</div>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Sunrise"><TextInput value={data.sunrise} onChange={(v) => u('sunrise', v)} placeholder="5:36 AM" /></Field>
        <Field label="Sunset"><TextInput value={data.sunset} onChange={(v) => u('sunset', v)} placeholder="7:42 PM" /></Field>
      </div>

      <div className="mt-4">
        <Field label="Notes (optional)" hint="A paragraph below the data — humanize the forecast.">
          <RichTextArea value={data.notes || ''} onChange={(v) => u('notes', v)} rows={3} placeholder="Bring a layer this afternoon — the wind off the river will be biting around 3pm." />
        </Field>
      </div>
    </>
  );
};

const StoryForm = ({ data, onChange, draftId }: FormProps) => {
  const u = (k: string, v: unknown) => onChange({ ...data, [k]: v });

  // Read through storyBodyItems so old drafts (paragraphs only) keep
  // showing their content; on first edit the form writes the canonical
  // `body` field and the legacy `paragraphs` field stops being read.
  const body = storyBodyItems(data);
  const setBody = (next: StoryBodyItem[]) => onChange({ ...data, body: next, paragraphs: undefined });

  return (
    <>
      <Field label="Hero image" hint="Top of the story. ~1200px wide is plenty for retina.">
        <ImagePicker
          value={{ url: data.imageUrl || '', alt: data.imageAlt || '', caption: data.caption || '' }}
          onChange={(v) => onChange({ ...data, imageUrl: v.url, imageAlt: v.alt, caption: v.caption })}
          draftId={draftId}
        />
      </Field>
      <Field label="Eyebrow" hint='Small label above headline. e.g. "Around Town"'>
        <TextInput value={data.eyebrow} onChange={(v) => u('eyebrow', v)} />
      </Field>
      <Field label="Headline"><TextArea value={data.headline} onChange={(v) => u('headline', v)} rows={2} /></Field>

      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 mt-4">Body</div>
      <BodyEditor items={body} onChange={setBody} draftId={draftId} />

      <div className="grid grid-cols-2 gap-2 mt-4">
        <Field label="Read more label"><TextInput value={data.readMoreLabel} onChange={(v) => u('readMoreLabel', v)} /></Field>
        <Field label="Read more URL"><TextInput value={data.readMoreUrl} onChange={(v) => u('readMoreUrl', v)} /></Field>
      </div>
      <Field label="Read more style" hint="Inline link reads like editorial; the button stands out more.">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => u('readMoreStyle', 'link')}
            className={`flex-1 px-3 py-2 text-sm rounded-md border transition-colors ${data.readMoreStyle !== 'button' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 hover:border-slate-300 text-slate-600'}`}
          >
            Inline link
          </button>
          <button
            type="button"
            onClick={() => u('readMoreStyle', 'button')}
            className={`flex-1 px-3 py-2 text-sm rounded-md border transition-colors ${data.readMoreStyle === 'button' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 hover:border-slate-300 text-slate-600'}`}
          >
            Button
          </button>
        </div>
      </Field>
    </>
  );
};

const SponsorForm = ({ data, onChange }: FormProps) => {
  const u = (k: string, v: unknown) => onChange({ ...data, [k]: v });
  return (
    <>
      <Field label="Sponsor name"><TextInput value={data.sponsorName} onChange={(v) => u('sponsorName', v)} /></Field>
      <Field label="Headline"><TextArea value={data.headline} onChange={(v) => u('headline', v)} rows={2} /></Field>
      <Field label="Body">
        <RichTextArea value={data.body || ''} onChange={(v) => u('body', v)} rows={4} />
      </Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="CTA label"><TextInput value={data.ctaLabel} onChange={(v) => u('ctaLabel', v)} /></Field>
        <Field label="CTA URL"><TextInput value={data.ctaUrl} onChange={(v) => u('ctaUrl', v)} /></Field>
      </div>
    </>
  );
};

const BulletinForm = ({ data, onChange }: FormProps) => {
  const u = (k: string, v: unknown) => onChange({ ...data, [k]: v });
  return (
    <>
      <Field label="Section title"><TextInput value={data.title} onChange={(v) => u('title', v)} /></Field>
      <Field label="Subtitle" hint="Short tagline; leave blank to hide."><TextInput value={data.subtitle} onChange={(v) => u('subtitle', v)} /></Field>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 mt-4">Items</div>
      <ArrayEditor
        items={data.items || []}
        onChange={(v) => u('items', v)}
        defaultItem={() => ({ headline: 'New item', body: '', sourceLabel: '', sourceUrl: '' })}
        addLabel="Add bulletin item"
        renderItem={(item: any, update) => (
          <>
            <input className="w-full mb-2 px-2 py-1.5 text-xs border border-slate-200 rounded bg-white font-semibold" value={item.headline} onChange={(e) => update({ ...item, headline: e.target.value })} placeholder="Item headline" />
            <RichTextArea value={item.body || ''} onChange={(v) => update({ ...item, body: v })} rows={2} placeholder="Summary" />
            <div className="mt-2 grid grid-cols-2 gap-2">
              <input className="px-2 py-1.5 text-xs border border-slate-200 rounded bg-white" value={item.sourceLabel} onChange={(e) => update({ ...item, sourceLabel: e.target.value })} placeholder="Source label" />
              <input className="px-2 py-1.5 text-xs border border-slate-200 rounded bg-white" value={item.sourceUrl} onChange={(e) => update({ ...item, sourceUrl: e.target.value })} placeholder="Source URL" />
            </div>
          </>
        )}
      />
    </>
  );
};

// Renders the per-event row used by both the manual ArrayEditor and
// the auto-mode "Add ad-hoc event" ArrayEditor.
function renderEventItem(item: any, update: (next: any) => void) {
  const isMultiDay = !!item.endDate;
  const allDay = !item.startTime;
  return (
    <div className="space-y-2">
      <input
        className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded bg-white font-semibold"
        value={item.name || ''}
        onChange={(e) => update({ ...item, name: e.target.value })}
        placeholder="Event name (e.g. The Garbologists Opening Night.)"
      />
      <textarea
        className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded bg-white resize-y"
        rows={2}
        value={item.description || ''}
        onChange={(e) => update({ ...item, description: e.target.value })}
        placeholder="One-sentence description (optional)"
      />
      <div className="grid grid-cols-2 gap-2">
        <input
          className="px-2 py-1.5 text-xs border border-slate-200 rounded bg-white"
          value={item.linkLabel || ''}
          onChange={(e) => update({ ...item, linkLabel: e.target.value })}
          placeholder="Link label (e.g. Get tickets)"
        />
        <input
          className="px-2 py-1.5 text-xs border border-slate-200 rounded bg-white"
          value={item.linkUrl ?? item.url ?? ''}
          onChange={(e) => update({ ...item, linkUrl: e.target.value })}
          placeholder="Link URL"
        />
      </div>

      <div className="flex gap-2 items-center pt-1">
        <input
          type="date"
          className="px-2 py-1.5 text-xs border border-slate-200 rounded bg-white"
          value={item.startDate || ''}
          onChange={(e) => update({ ...item, startDate: e.target.value })}
        />
        {!allDay ? (
          <input
            type="time"
            className="px-2 py-1.5 text-xs border border-slate-200 rounded bg-white"
            value={item.startTime || ''}
            onChange={(e) => update({ ...item, startTime: e.target.value })}
          />
        ) : (
          <span className="text-[11px] text-slate-500 italic">All day</span>
        )}
      </div>

      <div className="flex gap-3 text-[11px] text-slate-600">
        <label className="flex items-center gap-1 cursor-pointer">
          <input
            type="checkbox"
            checked={allDay}
            onChange={(e) => update({ ...item, startTime: e.target.checked ? '' : '20:00' })}
          />
          All day
        </label>
        <label className="flex items-center gap-1 cursor-pointer">
          <input
            type="checkbox"
            checked={isMultiDay}
            onChange={(e) => update({ ...item, endDate: e.target.checked ? (item.startDate || todayIso()) : '', endTime: '' })}
          />
          Multi-day
        </label>
      </div>

      {isMultiDay && (
        <div className="flex gap-2 items-center">
          <span className="text-[11px] text-slate-500 w-12">Through</span>
          <input
            type="date"
            className="px-2 py-1.5 text-xs border border-slate-200 rounded bg-white"
            value={item.endDate || ''}
            onChange={(e) => update({ ...item, endDate: e.target.value })}
          />
        </div>
      )}
    </div>
  );
}

const CalendarForm = ({ data, onChange }: FormProps) => {
  const u = (k: string, v: unknown) => onChange({ ...data, [k]: v });
  const dbEvents = useUpcomingEvents();
  const mode = resolveCalendarMode(data);
  const today = todayIso();
  const hiddenIds: string[] = Array.isArray(data.hiddenEventIds) ? data.hiddenEventIds : [];

  function switchToManual() {
    // Materialize whatever the block is currently rendering into the
    // legacy `events` array, drop the auto-mode keys, set source.
    const merged = resolveCalendarEvents(data, dbEvents).map((e) => {
      const { _dbId, ...clean } = e as any;
      return clean;
    });
    onChange({
      title: data.title,
      subtitle: data.subtitle,
      source: 'manual',
      events: merged,
    });
  }

  function switchToAuto() {
    onChange({
      title: data.title,
      subtitle: data.subtitle,
      source: 'auto',
      events: [],
      hiddenEventIds: [],
      extraEvents: [],
    });
  }

  function toggleHide(id: string) {
    const next = hiddenIds.includes(id)
      ? hiddenIds.filter((x) => x !== id)
      : [...hiddenIds, id];
    u('hiddenEventIds', next);
  }

  return (
    <>
      <Field label="Section title"><TextInput value={data.title} onChange={(v) => u('title', v)} /></Field>
      <Field label="Subtitle" hint="Short tagline; leave blank to hide."><TextInput value={data.subtitle} onChange={(v) => u('subtitle', v)} /></Field>

      {mode === 'auto' ? (
        <>
          <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 mb-3 text-xs text-blue-900">
            <span className="font-semibold">Auto-pull mode.</span> Events come from your{' '}
            <Link to="/events" className="underline">events page</Link>{' '}
            (next 8 days, hidden events excluded).
          </div>

          <Field label="Pulled events" hint="Untick to skip an event in this newsletter only.">
            {dbEvents.length === 0 ? (
              <p className="text-xs text-slate-500 italic px-1">
                No upcoming events on the events page. <Link to="/events" className="underline">Add some →</Link>
              </p>
            ) : (
              <ul className="space-y-1.5">
                {dbEvents.map((ev) => {
                  const hidden = hiddenIds.includes(ev.id);
                  const dateLabel = formatEventDate(
                    {
                      startDate: ev.startDate,
                      startTime: ev.startTime || undefined,
                      endDate: ev.endDate || undefined,
                      endTime: ev.endTime || undefined,
                    },
                    today,
                  );
                  return (
                    <li key={ev.id} className="flex items-start gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={!hidden}
                        onChange={() => toggleHide(ev.id)}
                        className="mt-0.5 h-3.5 w-3.5 flex-shrink-0"
                      />
                      <div className={`flex-1 min-w-0 ${hidden ? 'opacity-50 line-through' : ''}`}>
                        <span className="font-semibold text-slate-700">{dateLabel}:</span>{' '}
                        <span className="text-slate-900">{ev.name}</span>
                        {ev.description && <span className="text-slate-500"> {ev.description}</span>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Field>

          <Field label="Ad-hoc events" hint="Extra events for this newsletter only; they don't get added to the events page.">
            <ArrayEditor
              items={data.extraEvents || []}
              onChange={(v) => u('extraEvents', v)}
              defaultItem={() => ({ name: 'New event', description: '', linkLabel: '', linkUrl: '', startDate: todayIso(), startTime: '', endDate: '', endTime: '' })}
              addLabel="Add ad-hoc event"
              renderItem={renderEventItem}
            />
          </Field>

          <button
            type="button"
            onClick={switchToManual}
            className="text-xs text-slate-600 hover:text-slate-900 underline mt-2"
          >
            Switch this block to manual mode →
          </button>
        </>
      ) : (
        <>
          <p className="text-xs text-slate-500 mb-2 -mt-1">Events show in date order automatically. The "when" line phrases itself based on today's date — "Tonight at 8pm", "Saturday morning", "April 30 – May 2".</p>
          <ArrayEditor
            items={data.events || []}
            onChange={(v) => u('events', v)}
            defaultItem={() => ({ name: 'New event', description: '', linkLabel: '', linkUrl: '', startDate: todayIso(), startTime: '', endDate: '', endTime: '' })}
            addLabel="Add event"
            renderItem={renderEventItem}
          />
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 mt-3 text-xs text-slate-600">
            <span className="font-semibold">Manual mode.</span>{' '}
            <button
              type="button"
              onClick={switchToAuto}
              className="underline hover:text-slate-900"
            >
              Switch to auto-pull from the events page →
            </button>
          </div>
        </>
      )}
    </>
  );
};

const PollForm = ({ data, onChange }: FormProps) => {
  const u = (k: string, v: unknown) => onChange({ ...data, [k]: v });
  const prev = (data.previousResults || []) as Array<{ label: string; percent: number }>;
  const sum = prev.reduce((acc, r) => acc + (Number(r.percent) || 0), 0);
  return (
    <>
      <Field label="Question"><TextArea value={data.question} onChange={(v) => u('question', v)} rows={3} /></Field>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 mt-2">Options</div>
      <ArrayEditor
        items={data.options || []}
        onChange={(v) => u('options', v)}
        defaultItem={() => ({ label: 'New option', url: '#' })}
        addLabel="Add option"
        renderItem={(item: any, update) => (
          <div className="grid grid-cols-2 gap-2">
            <input className="px-2 py-1.5 text-xs border border-slate-200 rounded bg-white" value={item.label} onChange={(e) => update({ ...item, label: e.target.value })} placeholder="Option label" />
            <input className="px-2 py-1.5 text-xs border border-slate-200 rounded bg-white" value={item.url} onChange={(e) => update({ ...item, url: e.target.value })} placeholder="URL" />
          </div>
        )}
      />

      <div className="mt-6 pt-4 border-t border-slate-100">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Previous poll results <span className="text-slate-400 font-normal normal-case">(optional)</span></div>
        <p className="text-xs text-slate-500 mb-3">Show how readers voted on the last poll. Leave the question blank to hide the section entirely.</p>
        <Field label="Section label" hint="Headline above the previous results. Try 'Yesterday's poll' or 'Last week, you said'.">
          <TextInput value={data.previousLabel} onChange={(v) => u('previousLabel', v)} placeholder="Yesterday's poll" />
        </Field>
        <Field label="Previous question">
          <TextInput value={data.previousQuestion} onChange={(v) => u('previousQuestion', v)} placeholder="Yesterday we asked: …" />
        </Field>
        <ArrayEditor
          items={data.previousResults || []}
          onChange={(v) => u('previousResults', v)}
          defaultItem={() => ({ label: '', percent: 0 })}
          addLabel="Add result"
          renderItem={(item: any, update) => (
            <div className="grid grid-cols-[1fr_80px] gap-2">
              <input className="px-2 py-1.5 text-xs border border-slate-200 rounded bg-white" value={item.label} onChange={(e) => update({ ...item, label: e.target.value })} placeholder="Option label" />
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min={0}
                  max={100}
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded bg-white"
                  value={item.percent ?? 0}
                  onChange={(e) => update({ ...item, percent: Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0)) })}
                  placeholder="0"
                />
                <span className="text-xs text-slate-500">%</span>
              </div>
            </div>
          )}
        />
        {prev.length > 0 && sum !== 100 && (
          <p className="mt-2 text-xs text-red-600">Results add up to {sum}%. They should total 100%.</p>
        )}
      </div>
    </>
  );
};

const SignoffForm = ({ data, onChange }: FormProps) => {
  const u = (k: string, v: unknown) => onChange({ ...data, [k]: v });
  return (
    <>
      <Field label="Closing line">
        <RichTextArea value={data.closing || ''} onChange={(v) => u('closing', v)} rows={2} />
      </Field>
      <Field label="Signature"><TextInput value={data.signature} onChange={(v) => u('signature', v)} /></Field>
      <Field label="Forward prompt"><TextInput value={data.forwardPrompt} onChange={(v) => u('forwardPrompt', v)} /></Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Forward label"><TextInput value={data.forwardLabel} onChange={(v) => u('forwardLabel', v)} /></Field>
        <Field label="Forward URL"><TextInput value={data.forwardUrl} onChange={(v) => u('forwardUrl', v)} /></Field>
      </div>
    </>
  );
};

const FooterForm = ({ data, onChange }: FormProps) => {
  const u = (k: string, v: unknown) => onChange({ ...data, [k]: v });
  return (
    <>
      <Field label="Tagline"><TextInput value={data.tagline} onChange={(v) => u('tagline', v)} /></Field>
      <Field label="Mailing address" hint="Required by CAN-SPAM."><TextArea value={data.address} onChange={(v) => u('address', v)} rows={2} /></Field>

      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 mt-2">Reach the editor</div>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Email"><TextInput value={data.editorEmail} onChange={(v) => u('editorEmail', v)} placeholder="editor@penobscotmorning.com" /></Field>
        <Field label="Phone"><TextInput value={data.editorPhone} onChange={(v) => u('editorPhone', v)} placeholder="(207) 401-7710" /></Field>
      </div>

      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 mt-2">Social links</div>
      <ArrayEditor
        items={data.socials || []}
        onChange={(v) => u('socials', v)}
        defaultItem={() => ({ label: 'Link', url: '#' })}
        addLabel="Add social link"
        renderItem={(item: any, update) => (
          <div className="grid grid-cols-2 gap-2">
            <input className="px-2 py-1.5 text-xs border border-slate-200 rounded bg-white" value={item.label} onChange={(e) => update({ ...item, label: e.target.value })} placeholder="Label" />
            <input className="px-2 py-1.5 text-xs border border-slate-200 rounded bg-white" value={item.url} onChange={(e) => update({ ...item, url: e.target.value })} placeholder="URL" />
          </div>
        )}
      />
    </>
  );
};

export const EDIT_FORMS: Record<BlockType, React.ComponentType<FormProps>> = {
  header: HeaderForm,
  greeting: GreetingForm,
  weather: WeatherForm,
  story: StoryForm,
  sponsor: SponsorForm,
  bulletin: BulletinForm,
  calendar: CalendarForm,
  poll: PollForm,
  signoff: SignoffForm,
  footer: FooterForm,
};
